// The camera's accelerators: W A S D to pan, Q E to turn, T to tilt, F to fit.
//
// **Accelerators only — every one of them is a tile on the rail** (§28.6 and
// the camera rework's own rule: this game is heading for mobile, where there is
// no keyboard to learn). So this reads `viewActions` rather than mirroring it —
// the letters are that list's and so is what they do, and the pan keys are the
// only ones written here. A gesture that exists *only* here is a gesture half
// the players cannot reach.
//
// **Not the arrow keys and not Space.** The arrows already drive the history
// transport (`zones/HistoryTransportZone.tsx`), and a key that means two things
// depending on what has focus is the modal camera this rework removed.
//
// Lives beside the rail rather than in the shell, the way the transport's arrows
// live in the transport's own zone: the keys and the buttons are one control,
// so they are read and changed together.

import { useEffect, useRef, type RefObject } from "react";

import type { BoardCanvasHandle } from "./BoardCanvas";
import { inField } from "./keyGuard";
import type { ViewAction } from "./viewActions";

/**
 * The pan keys, as a screen-relative direction each.
 *
 * **Signed against `CameraRig.slide`, which negates**: `drive(right, …)` pushes
 * the *board* right, so the view goes left — a key that moves the view right
 * therefore passes a negative `right`. `forward` needs no flip: positive
 * carries the focus deeper into the board, which is up the screen. Verified
 * against the rig rather than assumed; a sign here is invisible until somebody
 * plays with it.
 *
 * A direction and not a distance: the rig owns the speed (`KEY_SPEED`), so a
 * held key glides at one rate rather than at whatever the platform's key
 * repeat happens to be.
 */
const PAN_KEYS: Record<string, { right: number; forward: number }> = {
  w: { right: 0, forward: 1 },
  s: { right: 0, forward: -1 },
  a: { right: 1, forward: 0 },
  d: { right: -1, forward: 0 }
};

export interface CameraKeys {
  board: RefObject<BoardCanvasHandle | null>;
  /**
   * The rail's own tiles, which is where the letters live (`viewActions.ts`).
   *
   * **The rail and the keys are one control**: the press is looked up here by
   * `key` and handed to `run`, the same callback the tiles are given, so the
   * mapping from an action to what it does is written once and a key that is
   * not a tile cannot exist. Only the pan keys are the hook's own — a pan key
   * is held down, which is not a tile.
   */
  actions: ViewAction[];
  run(id: ViewAction["id"]): void;
}

export function useCameraKeys({ board, actions, run }: CameraKeys): void {
  /**
   * Read through a ref so the listener is attached once. The actions are
   * rebuilt every render and so is the callback, and depending on either would
   * add and remove a window listener on every frame the camera moves.
   */
  const live = useRef({ board, actions, run });
  live.current = { board, actions, run };

  useEffect(() => {
    /**
     * Which pan keys are down, and the direction last sent to the rig.
     *
     * The set is the state and the direction is derived from it, because two
     * keys can be down at once and either one coming up leaves the other still
     * driving — tracking a direction alone loses that and sticks the camera on
     * whichever key was released second.
     */
    const held = new Set<string>();
    let driven = { right: 0, forward: 0 };

    const drive = () => {
      let right = 0;
      let forward = 0;
      for (const key of held) {
        const pan = PAN_KEYS[key];
        if (!pan) continue;
        right += pan.right;
        forward += pan.forward;
      }
      // Opposite keys cancel rather than fighting, and the rig is handed a
      // direction: the speed is its own (`KEY_SPEED`).
      right = Math.sign(right);
      forward = Math.sign(forward);
      if (right === driven.right && forward === driven.forward) return;
      driven = { right, forward };
      live.current.board.current?.driveCamera(right, forward);
    };

    const onKey = (event: KeyboardEvent) => {
      // No modifier: every one of these is a browser or platform shortcut with
      // one held, and `cmd+d` bookmarking the page is not a camera pan.
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (inField(event)) return;
      const { board, actions, run } = live.current;
      const handle = board.current;
      if (!handle) return;

      // Lower-cased, because the key is the letter and not the shift state —
      // though a held shift is refused above, a caps lock is not.
      const key = event.key.toLowerCase();
      const pan = PAN_KEYS[key];
      const action = pan ? undefined : actions.find((one) => one.key === key);
      if (pan) {
        // **`event.repeat` says nothing new.** The key is already in the set
        // and the glide is the rig's, so a repeat is not a second press — it
        // used to be the only thing that made a held key keep going.
        if (!event.repeat) {
          held.add(key);
          drive();
        }
      } else if (action) {
        run(action.id);
      } else {
        return;
      }
      // Only on a key we took: `preventDefault` on everything else is how a
      // page stops being typable.
      event.preventDefault();
    };

    /**
     * Letting go stops that key, whatever else is going on.
     *
     * **No modifier or field guard here**, unlike the press: a key taken while
     * the page was plain can be released with `cmd` held or after focus has
     * moved into a field, and a release that is refused leaves the camera
     * gliding with nothing on the keyboard to stop it.
     */
    const onUp = (event: KeyboardEvent) => {
      if (!held.delete(event.key.toLowerCase())) return;
      drive();
      event.preventDefault();
    };
    // The window losing focus never sends the `keyup`, so a tab switched away
    // from mid-glide comes back to a board still sliding.
    const onBlur = () => {
      if (!held.size) return;
      held.clear();
      drive();
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
      onBlur();
    };
  }, []);
}
